// Implements: F-IDN-017
/**
 * "Mis datos": download the JSON export, or erase the own personal data. An erasure closes every
 * session of the person, this one included, so on success the cache is emptied (nothing of the
 * person stays in memory) and the browser lands on `/login`, as a sign-out does.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { ApiError } from "../../../shared/api/ApiError";
import type { Download } from "../../../shared/api/httpClient";
import { emptySession } from "../../../shared/api/session";
import { downloadPersonalData, erasePersonalData } from "../api/accountApi";

export const LOGIN_ROUTE = "/login";

export interface UsePersonalDataResult {
  readonly download: UseMutationResult<Download, ApiError, string>;
  readonly erase: UseMutationResult<null, ApiError, string>;
}

export function usePersonalData(): UsePersonalDataResult {
  const client = useQueryClient();
  const navigate = useNavigate();
  const download = useMutation<Download, ApiError, string>({ mutationFn: downloadPersonalData });
  const erase = useMutation<null, ApiError, string>({
    mutationFn: async (userId) => {
      await erasePersonalData(userId);
      return null;
    },
    onSuccess: () => {
      // Leave the page first: a section still mounted while the cache empties would ask for the
      // erased profile again in the same render.
      void navigate(LOGIN_ROUTE, { replace: true });
      emptySession(client);
    },
  });
  return { download, erase };
}
